import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { gsap, Power1 } from 'gsap';
import Lottie from 'lottie-react';

import { SCREEN_SIZES } from '@/shared/Constants';
import { supportsTouch } from '@/playlist/games/laneChooser/Helpers';

import Tooltip, { TooltipColors, TooltipSizes } from '@/components/tooltip/Tooltip';

import keysInstruction from '@/playlist/games/letterParty/assets/lottie/keys-instruction.json';
import swipeInstruction from '@/playlist/games/letterParty/assets/lottie/swipe-instruction.json';

interface KeyInstructionsProps {
	show: boolean;
	onDismiss?: () => void;
}

const TOOLTIP_ANCHOR_ID = 'letter-party-key-instructions-tooltip-anchor';

const KeyInstructions = ({ show }: KeyInstructionsProps) => {
	const { t } = useTranslation();
	const [useSwipeInstructions, setUseSwipeInstructions] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const updateSwipeMode = () => {
			const prefersSwipe = window.innerWidth < SCREEN_SIZES.TABLET || supportsTouch();
			setUseSwipeInstructions(prefersSwipe);
		};
		updateSwipeMode();
		window.addEventListener('resize', updateSwipeMode);
		return () => window.removeEventListener('resize', updateSwipeMode);
	}, []);

	// Handle fade in/out with GSAP
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		if (show) {
			gsap.to(el, {
				autoAlpha: 1,
				duration: 0.3,
				ease: Power1.easeOut,
			});
		} else {
			gsap.to(el, {
				autoAlpha: 0,
				duration: 0.3,
				ease: Power1.easeOut,
			});
		}
	}, [show]);

	// Initialize as hidden
	useEffect(() => {
		if (containerRef.current) {
			gsap.set(containerRef.current, { autoAlpha: 0 });
		}
	}, []);

	const text = useSwipeInstructions
		? t('letter_party.hints.swipe')
		: t('letter_party.hints.arrow_keys');

	const animationData = useSwipeInstructions ? swipeInstruction : keysInstruction;

	return (
		<div ref={containerRef} className="letter-party-game__key-instructions">
			<div
				id={TOOLTIP_ANCHOR_ID}
				className="letter-party-game__key-instructions-content"
				role="note"
				aria-label={`Hint: ${text}`}
			>
				<Lottie
					className="letter-party-game__key-instructions-animation"
					animationData={animationData}
					loop
					autoplay
				/>
			</div>
			<Tooltip
				place="top"
				title={text}
				anchor={TOOLTIP_ANCHOR_ID}
				color={TooltipColors.LIGHT}
				size={TooltipSizes.LARGE}
				isOpen={show}
				className="letter-party-game__key-instructions-tooltip"
			/>
		</div>
	);
};

export default KeyInstructions;
